import { AuthenticationContext } from "@/app/context/authentication/authenticationContext";
import {
    Button, Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text, Image,
    Box,
    VStack,
    Avatar,
    HStack,
    Flex
} from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { useContext, useEffect, useRef, useState } from "react";
import { keyframes, css } from '@emotion/react';
import { allInGameCharactersConst } from "@/app/const/allInGameCharactersConst";
import TextToSpeechButton from "@/app/layout/global/textToSpeechButton";
import { GlobalDataContext } from "@/app/context/global/globalDataContext";
import { useRouter } from "next/router";
import { buildQuestUrl } from "@/app/helpers/buildUrlHelper";
import { GlobalDataUserContext } from "@/app/context/global/globalDataUserContext";
import { AllQuestModelConst } from "@/app/const/allQuestModelConst";
const zoomAndShake = keyframes`
   0% {
   -webkit-transform: scale(0.9);
    transform: scale(0.9);
    transform-origin: center;
    opacity: 0;
  }
  50% {
   -webkit-transform: scale(1.2);
    transform: scale(1.2);
    transform-origin: center;
    opacity: 1;
  }
  100% {
   -webkit-transform: scale(1);
    transform: scale(1);
    transform-origin: center;
    opacity: 1;
  }
`;

type Props = {
    characterId: string
    isOpen: boolean;
    onClose: () => void;
};

export default function CharacterTalkingQuestModal({characterId, isOpen,onClose}:Props) {

    const { t } = useTranslation();
    const router = useRouter();
    const {language} = router.query
    const conversationScrollBottom = useRef(null);
    const scrollToBottom = ()=> {
        if (conversationScrollBottom.current !==null) {
            const element = conversationScrollBottom.current as Element;
            element.scrollIntoView()
        }
    }
    const {
        showLoginModal,
        setShowLoginModal,
        user,
        //subscribeNewsletter,
        //setSubscribeNewsletter
    } = useContext(AuthenticationContext)

    const {
        categoryId
    } = useContext(GlobalDataContext)

    const {
        myArtifacts
    } = useContext(GlobalDataUserContext)

    const character = allInGameCharactersConst.find(character => character.id === characterId)

    const [actualMessageIndex, setActualMessageIndex] = useState(1)
    const [localMessages, setLocalMessages] = useState<any>([])
    const [voiceTalking, setVoiceTalking] = useState(false)
    const [forceStopTalking, setForceStopTalking] = useState(false)    
    const [alreadyGotArtifact, setAlreadyGotArtifact] = useState(false)
    
    useEffect(() => {
        scrollToBottom()
    }, [localMessages])

    useEffect(() => {
        if ( character ){
            if ( !alreadyGotArtifact) {
                setLocalMessages(character.messages.slice(0, actualMessageIndex))
            }else{
                setLocalMessages([character.successMessage])
            }
        }        
    }, [actualMessageIndex,character, language, alreadyGotArtifact])

    useEffect(()=>{
        if ( myArtifacts && character ){
            const artifactId = getArtefactFromCharacter(character.id)
            if ( artifactId && myArtifacts.find(a=>a.id===artifactId)){
                setAlreadyGotArtifact(true)
            }
        }
    }, [myArtifacts, character])

    const goToQuest = () => {
        router.push(buildQuestUrl(language as string, categoryId, character?.questId,"s"), undefined, { shallow: true }).then(()=>{
            onClose();
        })

    }

    const getArtefactFromCharacter = (characterId:string)=>{
        const character = allInGameCharactersConst.find(c=>c.id===characterId)
        if ( !character ) return false
        const quest = AllQuestModelConst.find(q=>q.id===character.questId)
        if ( quest && quest.artifact ){
          return quest.artifact.id
        }
        return false
    }
      
    return (
        <Modal isCentered  isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent maxH="80vh" overflow={"hidden"} m={5}  css={css`
                    animation: ${zoomAndShake} 0.7s ease-out;
                `}>
               <ModalCloseButton />
                <ModalHeader>
                    <VStack>
                        <Avatar boxShadow='lg' size="lg" src={(character?.voiceTalkingUrl&&voiceTalking)?character.voiceTalkingUrl:character?.url} icon={undefined}/>
                        <Text>{character?.name}</Text>
                    </VStack>
                </ModalHeader>
                <ModalBody px={2} pt={0} pb={2} overflow="auto">                    
                    <VStack gap={2} alignItems={"end"} pl={8}>
                        {localMessages.map((message:any, index:number) => (
                            <div key={index} >
                                <HStack gap={1}>
                                    <Flex   
                                        direction={"column"}                                        
                                        bg={"gray.200"}                    
                                        minW="100px"
                                        maxW="350px"
                                        p={2}
                                        borderRadius={"md"}
                                        zIndex={2}
                                    >
                                        <Text fontSize="sm">{message[language as string]}</Text>
                                        {user && <TextToSpeechButton
                                            voiceId={character?.voiceId as string}
                                            text={message[language as string]}
                                            voiceSettings={{
                                                stability: 0,
                                                similarity_boost: 0,
                                            }}
                                            setVoiceTalking={setVoiceTalking}
                                            forceStopTalking={forceStopTalking}
                                            setForceStopTalking={setForceStopTalking}
                                            autoPlay={ actualMessageIndex === index +1} 
                                        />}                                                                      
                                    </Flex>
                                    <Avatar bottom={1} alignSelf="self-end" size="2xs" src={character?.url}  icon={undefined}/>
                                </HStack>
                            </div>
                        ))}                        
                    </VStack>
                    <div style={{height:0}} ref={conversationScrollBottom}></div>
                </ModalBody>
                    {!alreadyGotArtifact && <ModalFooter px={2} pt={4} pb={2} display="flex">
                        {(actualMessageIndex < (character?.messages?.length ?? 0 -1))  && <Button
                            colorScheme="linkedin"
                            onClick={()=>setActualMessageIndex(actualMessageIndex + 1)}
                            size="sm"
                        >
                            {t("quest.next")}
                        </Button>}
                        {(actualMessageIndex === (character?.messages?.length ?? 0 -1))  && <Button
                            background="#6c27b7!important"
                            color="white!important"
                            onClick={()=>goToQuest()}
                            size="sm"
                        >
                            {t("quest.doQuest")}
                        </Button>}
                    </ModalFooter>}
                    {alreadyGotArtifact && <ModalFooter px={2} pt={4} pb={2} display="flex">
                        <Button
                            colorScheme="linkedin"
                            onClick={onClose}
                            size="sm"
                        >
                            {t("game.close")}
                        </Button>
                    </ModalFooter>}
                </ModalContent>
            </Modal>
    )

}